import React from "react";

interface Segment { // TODO merge types with server
  color?: string;
  value: number;
  label?: number|string;
}

type GraphData = Record<string, number | Segment[]>;

interface BarGraphProps {
  data: GraphData;
  formatValue?: (v: number) => string;
  formatKey?: (v: string|number) => string;
  horizontal?: boolean;
  defaultColor?: string;
  // gapRatio?: number;
  sort?: boolean,
  style?: any,
}

export const BarGraph: React.FC<BarGraphProps> = ({
  data,
  formatValue = (v) => v.toFixed(2),
  formatKey = (v) => v,
  horizontal = false,
  defaultColor = "#ff195e",
  sort = true,
  style = {},
}) => {
  // Constants and dimensions
  const viewBoxWidth = 1280;
  const viewBoxHeight = 720;  
  const keys = Object.keys(data??{});
  if (!keys.length) return <div style={{ width: '100%', height: '100%' }} />;
  const getValueTotal=(v: number | Segment[])=>Array.isArray(v)?v.map(v=>v.value).reduce((t,v)=>t+v,0):v

  // Calculate min/max values
  const values = Object.values(data).map(value => getValueTotal(value));
  const maxVal = Math.max(1,...values);
  const minVal = Math.min(-0.01,...values);
  // Layout constants
  const marginRatio = horizontal ? 0.15 : 0.05;
  const drawAreaWidth = viewBoxWidth * (1 - 2*marginRatio);
  const drawAreaHeight = viewBoxHeight * (1 - 2*marginRatio);

  // Bar layout calculations
  const barCount = keys.length;
  const delta = maxVal-minVal
  const preSorted = (sort ? keys.sort((a,b)=>getValueTotal(data[a]) - getValueTotal(data[b])) : keys).filter(key => !isNaN(getValueTotal(data[key])))
  const sorted = horizontal ? preSorted : preSorted.reverse()

  // Position a value in the draw area, with margins
  const marginX = (v) => (marginRatio*viewBoxWidth) + v
  const marginY = (v) => (marginRatio*viewBoxHeight) + v
  const inDrawX = (v) => v * drawAreaWidth
  const inDrawY = (v) => v * drawAreaHeight
  const Val = (v) => (v-minVal)/delta

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <svg
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height: '100%', fontSize: '0.8em', fontWeight: 'normal', ...style }}
      >
        {/* Zero line */}
        <line 
          x1={marginX(inDrawX(horizontal ? Val(0) : 0))}
          x2={marginX(inDrawX(horizontal ? Val(0) : 1))}
          y1={marginY(inDrawY(1-(horizontal ? 0 : Val(0))))}
          y2={marginY(inDrawY(1-(horizontal ? 1 : Val(0))))}
          stroke="#333"
        />
        {sorted.map((key, barN) => {
          const value = data[key];
          const segments = Array.isArray(value) ? value : [{value, color:defaultColor}]
          // TODO generalize this with a function PointsToPointAndLength
          const zeroLo = barN/(barCount) // * barThicknessTotal + gapWidth
          const zeroHi = (barN+1)/(barCount)
          const ZPos = (Math.min(zeroLo,zeroHi))
          const ZPos2 = (Math.max(zeroLo,zeroHi))
          const ZL = Math.abs(ZPos2-ZPos)
          const ZMid = ZPos + ZL/2
          const ZGap = ZL*0.05

          const isPositive = segments[0].value >= 0;
          const VKey = horizontal ? -0.005 : -0.02 // TODO move const out to an option called KeyVDistance

          const barsWithLabels = segments.map(({value,color=defaultColor,label}, i, all) => {
            const last = (all.length - 1 )> i

            const prevSegVal = all.slice(0,i).reduce((t,v)=>t+v.value,0)
            const valueLo = prevSegVal
            const valueHi = (valueLo+value) - ((last||(value==0)) ? 0 : 0.01) // TODO move 0.01 to "segmentGap"

            // TODO generalize this with a function PointsToPointAndLength
            const VPos = Val(Math.min(valueLo,valueHi))
            const VPos2 = Val(Math.max(valueLo,valueHi))
            const VL = VPos2 - VPos
            const VLabel = VPos2 + 0.01 // TODO move const out to an option called LabelVDistance

            const H =inDrawY((horizontal ? (ZL-ZGap) : VL))
            return [
              <rect
                key={i}
                x={ marginX(inDrawX(horizontal ? VPos : (ZPos+ZGap))) }
                y={ marginY(inDrawY(1-(horizontal ? ZPos+ZGap : VPos))) - H }
                width={inDrawX((horizontal ? VL : (ZL-ZGap)))}
                height={H} 
                fill={color || defaultColor}  
              />, // Value Label
              <text
                key={i}
                x={ marginX(inDrawX(horizontal ? (VLabel) : ZMid)) }
                y={ marginY(inDrawY(1-(horizontal ? ZMid : (VLabel)))) }
                textAnchor={horizontal ? (isPositive ? "start" : "end") : "middle"}
                fill="#000"
              >
                {typeof label == 'string' ? label : formatValue(label ?? value)}
              </text>
            ];
          })

          return (
            <g key={barN}>
              {/* Bar segments */}
              {barsWithLabels.map(([bar])=>bar)}
              {barsWithLabels.map(([bar,label])=>label)}
              
              {/* Key */}
              <text
                x={ marginX(inDrawX(horizontal ? VKey : ZMid)) }
                y={ marginY(inDrawY(1-(horizontal ? ZMid : VKey))) }
                textAnchor={horizontal ? "end" : "middle"}
                fill="#000"
              >
                {formatKey(key)}
              </text>
              
            </g>  
          );
        })}
      </svg>
    </div>
  );
};
